/* =========================================================
   Desert City Games – DCG Top Singles
   File: assets/dcg-top-singles.js

   Features:
   - Tab switching (click + keyboard arrows)
   - Infinite looping rail (clones at both ends, seamless reset)
   - Variant select → hidden input sync + price update + foil state
   - Add-to-cart via DCG cart bridge + theme-native $.notify() toast
   - Focus/dim scroll effect (desktop only, scroll-position based)
   - Narrow-mode detection for Shopify editor preview
========================================================= */

(() => {
  "use strict";

  /* ---------------------------------------------------
     DCG Cart Bridge
     Piggybacks theme: /cart/add.js + adjustCartDropDown()
  --------------------------------------------------- */
  (function ensureDcgCartBridge() {
    window.DCG = window.DCG || {};
    window.DCG.cart = window.DCG.cart || {};
    if (typeof window.DCG.cart.addItem === "function") return;

    window.DCG.cart.addItem = async function ({ id, quantity = 1, properties } = {}) {
      if (!id) throw new Error("Missing variant id");

      // ── Pre-flight enforcement (mirrors dcg-cart-bridge.js) ────────────────
      // Ensures limit:N tags are honored even on pages where the full cart
      // bridge script isn't loaded (e.g. the homepage Top Singles rail).
      // window.DCG.enforcer is defined globally by dcg-cart-enforcer.js in
      // <head>. Without this, the fallback add path skipped all limit checks,
      // so enforcement depended on which script happened to load first.
      if (window.DCG && typeof window.DCG.enforcer === "object" && window.DCG.enforcer) {
        let _enforcerBlocked = false;
        try {
          const check = await window.DCG.enforcer.preflight(id, quantity);
          if (check.blocked) {
            _enforcerBlocked = true;
            window.DCG.enforcer.showToast(check.message || "Purchase limit reached.");
            throw new Error(check.message || "Purchase limit reached.");
          }
          if (check.message) {
            window.DCG.enforcer.showToast(check.message);
          }
          quantity = check.qty;
        } catch (enforceErr) {
          if (_enforcerBlocked) throw enforceErr;
          console.warn("[DCG] enforcer preflight error — allowing add:", enforceErr);
        }
      }

      const payload = { id: Number(id), quantity: Number(quantity || 1) };
      if (properties) payload.properties = properties;

      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        try {
          const data = await res.json();
          throw new Error(data?.description || data?.message || "Add to cart failed");
        } catch (_) {
          const txt = await res.text().catch(() => "");
          throw new Error(txt || "Add to cart failed");
        }
      }

      const data = await res.json();

      if (typeof window.adjustCartDropDown === "function") {
        window.adjustCartDropDown();
      } else {
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      }

      // Always dispatch cart:updated so dcg-cart-enforcer.js can run its
      // post-add drawer audit regardless of which refresh path was taken.
      document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true }));

      return data;
    };
  })();


  /* ---------------------------------------------------
     Utilities
  --------------------------------------------------- */
  const ROOT_SEL = ".dcg-pb";
  const FOIL_RE = /foil/i;
  const MOBILE_W = 749;

  function qs(sel, root = document) { return root.querySelector(sel); }
  function qsa(sel, root = document) { return Array.from(root.querySelectorAll(sel)); }
  function isMobile() { return window.innerWidth <= MOBILE_W; }


  /* ---------------------------------------------------
     Narrow-mode detection
     Uses rendered width for Shopify editor "mobile preview"
  --------------------------------------------------- */
  function applyNarrowMode(root) {
    if (!root) return;
    const w = root.getBoundingClientRect().width;
    root.classList.toggle("dcg-is-narrow", w > 0 && w <= MOBILE_W);
  }

  function bindNarrowMode(root) {
    if (!root || root.__dcgNarrowBound) return;
    root.__dcgNarrowBound = true;

    let raf = 0;
    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => applyNarrowMode(root));
    };

    schedule();
    window.addEventListener("resize", schedule, { passive: true });

    if ("ResizeObserver" in window) {
      const ro = new ResizeObserver(schedule);
      ro.observe(root);
    }
  }


  /* ---------------------------------------------------
     Tabs
  --------------------------------------------------- */
  function hydratePanel(panel) {
    const tpl = panel.querySelector(":scope > template[data-dcg-panel-template]");
    if (!tpl) return false;
    panel.appendChild(tpl.content);
    tpl.remove();
    initFoilForAllCards(panel);
    return true;
  }

  function setActiveTab(root, tabTargetId) {
    qsa(".dcg-pb__tab", root).forEach((btn) => {
      const active = btn.dataset.tabTarget === tabTargetId;
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-selected", active ? "true" : "false");
      btn.tabIndex = active ? 0 : -1;
    });

    qsa(".dcg-pb__panel", root).forEach((panel) => {
      const active = panel.dataset.panel === tabTargetId;
      panel.classList.toggle("is-active", active);
      if (active) {
        panel.removeAttribute("hidden");
        hydratePanel(panel);
        // Re-init focus/dim and looping for newly visible panel
        initFocusDimForPanel(root, panel);
        initLoopingRail(panel);
      } else {
        panel.setAttribute("hidden", "");
      }
    });
  }

  function bindTabKeyboard(root) {
    const tablist = qs(".dcg-pb__tabs", root);
    if (!tablist || tablist.__dcgKeyBound) return;
    tablist.__dcgKeyBound = true;

    tablist.addEventListener("keydown", (e) => {
      const tabs = qsa(".dcg-pb__tab", tablist);
      if (!tabs.length) return;

      const current = tabs.findIndex((t) => t.classList.contains("is-active"));
      let next = -1;

      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        next = (current + 1) % tabs.length;
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        next = (current - 1 + tabs.length) % tabs.length;
      } else if (e.key === "Home") {
        next = 0;
      } else if (e.key === "End") {
        next = tabs.length - 1;
      }

      if (next >= 0) {
        e.preventDefault();
        tabs[next].focus();
        const target = tabs[next].dataset.tabTarget;
        if (target) setActiveTab(root, target);
      }
    });
  }


  /* ---------------------------------------------------
     Rail scrolling
  --------------------------------------------------- */
  function scrollRail(panelEl, dir) {
    const rail = qs(".dcg-pb__rail", panelEl);
    if (!rail) return;

    const card = qs(".dcg-pb__card:not([data-dcg-clone])", rail);
    const cardW = card ? card.offsetWidth : 240;
    const gap = parseInt(getComputedStyle(rail).gap) || 16;
    const amount = Math.max(180, Math.round(cardW + gap));
    rail.scrollBy({ left: dir * amount, behavior: "smooth" });
  }


  /* ---------------------------------------------------
     Infinite loop rail
     Clones cards at both ends and seamlessly resets
     scroll position when entering clone territory.
  --------------------------------------------------- */
  function initLoopingRail(panel) {
    const rail = qs(".dcg-pb__rail", panel);
    if (!rail || rail.__dcgLoopInit) return;

    const realCards = qsa(".dcg-pb__card:not([data-dcg-clone])", rail);
    if (realCards.length < 3) return; // not enough to loop

    rail.__dcgLoopInit = true;
    rail.classList.add("dcg-pb__rail--looping");

    // Determine how many clones to buffer (fill ~1.5 viewports)
    const gap = parseInt(getComputedStyle(rail).gap) || 16;
    const cardW = realCards[0].offsetWidth + gap;
    const viewW = rail.clientWidth;
    const bufferCount = Math.min(realCards.length, Math.ceil((viewW * 1.5) / cardW) + 1);

    // Clone tail cards → prepend (for scrolling left past start)
    const prependFrag = document.createDocumentFragment();
    for (let i = realCards.length - bufferCount; i < realCards.length; i++) {
      const clone = realCards[i].cloneNode(true);
      clone.setAttribute("data-dcg-clone", "tail");
      clone.removeAttribute("id");
      // Disable forms inside clones
      qsa("form", clone).forEach((f) => f.removeAttribute("action"));
      qsa("[data-dcg-atc]", clone).forEach((b) => { b.disabled = true; });
      prependFrag.appendChild(clone);
    }

    // Clone head cards → append (for scrolling right past end)
    const appendFrag = document.createDocumentFragment();
    for (let i = 0; i < bufferCount; i++) {
      const clone = realCards[i].cloneNode(true);
      clone.setAttribute("data-dcg-clone", "head");
      clone.removeAttribute("id");
      qsa("form", clone).forEach((f) => f.removeAttribute("action"));
      qsa("[data-dcg-atc]", clone).forEach((b) => { b.disabled = true; });
      appendFrag.appendChild(clone);
    }

    rail.prepend(prependFrag);
    rail.append(appendFrag);

    // Calculate the scroll offset where the "real" cards begin
    const realStartOffset = bufferCount * cardW;

    // Jump to the real start (no animation)
    rail.scrollLeft = realStartOffset;

    // Store metrics for the scroll handler
    rail.__dcgLoop = {
      bufferCount,
      cardW,
      realCount: realCards.length,
      realStartOffset,
      realEndOffset: realStartOffset + realCards.length * cardW,
    };

    // Scroll handler: seamless jump when entering clone territory
    let jumping = false;
    const onScroll = () => {
      if (jumping) return;
      const loop = rail.__dcgLoop;
      const sl = rail.scrollLeft;

      // Scrolled into prepended clones (past the left edge)
      if (sl < loop.realStartOffset - loop.cardW * 0.5) {
        jumping = true;
        const overshoot = loop.realStartOffset - sl;
        rail.scrollLeft = loop.realEndOffset - overshoot - loop.cardW;
        requestAnimationFrame(() => { jumping = false; });
      }
      // Scrolled into appended clones (past the right edge)
      else if (sl > loop.realEndOffset - rail.clientWidth + loop.cardW * 0.5) {
        jumping = true;
        const overshoot = sl - (loop.realEndOffset - rail.clientWidth);
        rail.scrollLeft = loop.realStartOffset + overshoot - loop.cardW;
        requestAnimationFrame(() => { jumping = false; });
      }
    };

    rail.addEventListener("scroll", onScroll, { passive: true });

    rail.__dcgLoopCleanup = () => {
      rail.removeEventListener("scroll", onScroll);
      // Remove clones
      qsa("[data-dcg-clone]", rail).forEach((c) => c.remove());
      rail.classList.remove("dcg-pb__rail--looping");
      rail.__dcgLoopInit = false;
    };
  }


  /* ---------------------------------------------------
     Focus / Dim (desktop only)
     Scroll-position based: measures which card's center
     is closest to the rail viewport center.
     Immune to transform feedback loops (scroll position
     doesn't change when we scale a card).
  --------------------------------------------------- */
  function updateFocusDim(root, panel) {
    const rail = qs(".dcg-pb__rail", panel);
    if (!rail) return;

    // Include all cards (real + clones) since clones are visible
    const cards = qsa(".dcg-pb__card", rail);
    if (!cards.length) return;

    const style = getComputedStyle(root);
    const dimEnabled = style.getPropertyValue("--dcg-focus-dim").trim() === "true";

    // Rail viewport center in page coordinates
    const railRect = rail.getBoundingClientRect();
    const viewCenterX = railRect.left + railRect.width / 2;

    // Find which card's center is closest to viewport center
    let bestCard = null;
    let bestDist = Infinity;

    cards.forEach((card) => {
      // Use offsetLeft + offsetWidth (layout position, not affected by transforms)
      const cardCenterX = card.offsetLeft - rail.scrollLeft + card.offsetWidth / 2 + railRect.left;
      const dist = Math.abs(cardCenterX - viewCenterX);
      if (dist < bestDist) {
        bestDist = dist;
        bestCard = card;
      }
    });

    // Only apply focus if the best card is reasonably near center
    // (half a card width threshold — don't focus when scrolled to extremes)
    const threshold = (bestCard?.offsetWidth || 240) * 0.8;
    const shouldFocus = bestDist < threshold;

    cards.forEach((card) => {
      const isBest = card === bestCard && shouldFocus;
      card.classList.toggle("is-focused", isBest);
      if (dimEnabled) {
        card.classList.toggle("is-dimmed", !isBest && shouldFocus);
      }
    });
  }

  function initFocusDimForPanel(root, panel) {
    if (isMobile()) return;

    const style = getComputedStyle(root);
    const enabled = style.getPropertyValue("--dcg-focus-enabled").trim();
    if (enabled !== "true") return;

    const rail = qs(".dcg-pb__rail", panel);
    if (!rail) return;

    // Tear down previous listener if any
    if (rail.__dcgFocusCleanup) rail.__dcgFocusCleanup();

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateFocusDim(root, panel);
        ticking = false;
      });
    };

    rail.addEventListener("scroll", onScroll, { passive: true });

    // Initial calculation
    requestAnimationFrame(() => updateFocusDim(root, panel));

    rail.__dcgFocusCleanup = () => {
      rail.removeEventListener("scroll", onScroll);
      qsa(".dcg-pb__card", rail).forEach((card) => {
        card.classList.remove("is-focused", "is-dimmed");
      });
    };
  }

  // Clean up focus/dim on resize crossing the mobile threshold
  function bindFocusDimResize(root) {
    if (root.__dcgFocusResizeBound) return;
    root.__dcgFocusResizeBound = true;

    let wasMobile = isMobile();
    window.addEventListener("resize", () => {
      const nowMobile = isMobile();
      if (wasMobile !== nowMobile) {
        wasMobile = nowMobile;
        // Re-init or teardown on all visible panels
        qsa(".dcg-pb__panel.is-active", root).forEach((panel) => {
          const rail = qs(".dcg-pb__rail", panel);
          if (nowMobile && rail?.__dcgFocusCleanup) {
            rail.__dcgFocusCleanup();
          } else if (!nowMobile) {
            initFocusDimForPanel(root, panel);
          }
        });
      }
    }, { passive: true });
  }


  /* ---------------------------------------------------
     Foil state
  --------------------------------------------------- */
  function updateFoilState(card, selectedTitle) {
    if (!card) return;
    card.classList.toggle("contains-foil", FOIL_RE.test(selectedTitle || ""));
  }

  function initFoilForAllCards(root) {
    qsa("[data-product-card]", root).forEach((card) => {
      const select = qs("[data-dcg-variant-select]", card);
      if (!select) return;
      const opt = select.options[select.selectedIndex];
      updateFoilState(card, opt?.dataset?.title || opt?.textContent || "");
    });
  }


  /* ---------------------------------------------------
     Variant select change handler
     Syncs: hidden select, ATC button, foil state, price
  --------------------------------------------------- */
  function bindVariantSync(root) {
    if (root.__dcgVariantBound) return;
    root.__dcgVariantBound = true;

    root.addEventListener("change", (e) => {
      const select = e.target.closest("[data-dcg-variant-select]");
      if (!select) return;

      const card = select.closest("[data-product-card]");
      if (!card) return;

      const opt = select.options[select.selectedIndex];
      const title = (opt?.dataset?.title || opt?.textContent || "").trim();

      // Sync hidden form select
      const hiddenSel = qs("select[name='id'][data-dcg-variant-input]", card);
      if (hiddenSel) hiddenSel.value = select.value;

      // Toggle ATC based on availability
      if (opt?.dataset?.available != null) {
        const avail = opt.dataset.available === "1";
        const btn = qs("[data-dcg-atc]", card);
        if (btn) btn.disabled = !avail;
      }

      // Update displayed price
      const priceEl = qs("[data-dcg-price]", card);
      if (priceEl && opt?.dataset?.price) {
        const compare = opt.dataset.compare || "";
        if (compare) {
          priceEl.innerHTML =
            '<s class="price-item--regular" style="opacity:.5">' + compare + "</s> " +
            '<span class="price-item--sale">' + opt.dataset.price + "</span>";
        } else {
          priceEl.innerHTML =
            '<span class="price-item">' + opt.dataset.price + "</span>";
        }
      }

      // Foil hook
      updateFoilState(card, title);
    });
  }


  /* ---------------------------------------------------
     ATC click shield + submit interceptor
     Capture-phase isolation from theme/app handlers
  --------------------------------------------------- */
  function bindClickShield() {
    if (document.__dcgTopSinglesClickBound) return;
    document.__dcgTopSinglesClickBound = true;

    document.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-dcg-atc]");
      if (!btn) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();

      const form = btn.closest("form");
      if (form) form.requestSubmit();
    }, true);
  }

  function bindSubmitInterceptor() {
    if (document.__dcgTopSinglesSubmitBound) return;
    document.__dcgTopSinglesSubmitBound = true;

    document.addEventListener("submit", async (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.classList.contains("dcg-pb__cartForm")) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();

      const btn = form.querySelector("[data-dcg-atc]");

      const idEl = form.querySelector("select[name='id']");
      const variantId = idEl?.value;

      if (!variantId) return;

      const originalLabel = btn ? btn.textContent : "";
      if (btn) {
        btn.disabled = true;
        btn.textContent = "Adding\u2026";
      }

      try {
        const item = await window.DCG.cart.addItem({ id: variantId, quantity: 1 });

        // Fire theme-native $.notify() toast (matches quickview.js pattern)
        if (typeof $ !== "undefined" && typeof $.notify === "function") {
          const successPrefix = $("#cartmessage .title-success").text() || "Added to cart: ";
          const link = '<a href="' + (item.url || "") + '">' + (item.title || "Item") + '</a>';
          const msg = successPrefix + link + " - " + (item.quantity || 1) + " x " +
            (typeof Shopify !== "undefined" && Shopify.formatMoney
              ? Shopify.formatMoney(item.price, Shopify.money_format)
              : "");

          $.notify(
            { message: msg },
            {
              type: "success",
              offset: 0,
              placement: { from: "top", align: "center" },
              z_index: 9999,
              animate: {
                enter: "animated fadeInDown",
                exit: "animated fadeOutUp",
              },
              template:
                '<div data-notify="container" class="col-xs-12 alert alert-{0}" role="alert">' +
                '<button type="button" aria-hidden="true" class="close" data-notify="dismiss">\u00d7</button>' +
                '<span data-notify="icon"></span> <span data-notify="title">{1}</span> ' +
                '<span data-notify="message">{2}</span>' +
                '<div class="progress" data-notify="progressbar">' +
                '<div class="progress-bar progress-bar-{0}" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100" style="width: 0%;"></div>' +
                '</div><a href="{3}" target="{4}" data-notify="url"></a></div>',
            }
          );
        }
      } catch (err) {
        console.warn("[DCG Top Singles] Add error:", err);

        // Fire theme-native error toast
        if (typeof $ !== "undefined" && typeof $.notify === "function") {
          const failPrefix = $("#cartmessage .title-failed").text() || "Could not add: ";
          $.notify(
            { message: failPrefix + (err?.message || "Please try again.") },
            {
              type: "danger",
              offset: 0,
              placement: { from: "top", align: "center" },
              z_index: 9999,
              animate: {
                enter: "animated fadeInDown",
                exit: "animated fadeOutUp",
              },
              template:
                '<div data-notify="container" class="col-xs-12 alert alert-{0}" role="alert">' +
                '<button type="button" aria-hidden="true" class="close" data-notify="dismiss">\u00d7</button>' +
                '<span data-notify="icon"></span> <span data-notify="title">{1}</span> ' +
                '<span data-notify="message">{2}</span>' +
                '<div class="progress" data-notify="progressbar">' +
                '<div class="progress-bar progress-bar-{0}" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100" style="width: 0%;"></div>' +
                '</div><a href="{3}" target="{4}" data-notify="url"></a></div>',
            }
          );
        }
      } finally {
        if (btn) {
          btn.disabled = false;
          btn.textContent = originalLabel || "Add to cart";
        }
      }
    }, true);
  }


  /* ---------------------------------------------------
     Per-section init
  --------------------------------------------------- */
  function initOne(root) {
    bindNarrowMode(root);

    // Tab click
    const tablist = qs(".dcg-pb__tabs", root);
    if (tablist && !tablist.__dcgBound) {
      tablist.__dcgBound = true;
      tablist.addEventListener("click", (e) => {
        const btn = e.target.closest(".dcg-pb__tab");
        if (!btn) return;
        const target = btn.dataset.tabTarget;
        if (target) setActiveTab(root, target);
      });
    }

    // Tab keyboard
    bindTabKeyboard(root);

    // Set initial tabindex on tabs
    qsa(".dcg-pb__tab", root).forEach((btn) => {
      btn.tabIndex = btn.classList.contains("is-active") ? 0 : -1;
    });

    // Rail arrow clicks
    if (!root.__dcgNavBound) {
      root.__dcgNavBound = true;
      root.addEventListener("click", (e) => {
        const prev = e.target.closest(".dcg-pb__nav--prev");
        const next = e.target.closest(".dcg-pb__nav--next");
        if (!prev && !next) return;

        const panel = e.target.closest(".dcg-pb__panel");
        if (!panel) return;
        scrollRail(panel, prev ? -1 : 1);
      });
    }

    // Rail scroll boundary detection
    // (handled by looping rail — no boundary detection needed)

    // Foil state init
    initFoilForAllCards(root);

    // Variant sync
    bindVariantSync(root);

    // Focus/dim + looping for active panel(s)
    qsa(".dcg-pb__panel.is-active", root).forEach((panel) => {
      initLoopingRail(panel);
      initFocusDimForPanel(root, panel);
    });

    bindFocusDimResize(root);
  }

  function initAll() {
    bindClickShield();
    bindSubmitInterceptor();
    qsa(ROOT_SEL).forEach(initOne);
  }

  document.addEventListener("DOMContentLoaded", initAll);
  document.addEventListener("shopify:section:load", initAll);
})();
